import { useState, useMemo, useCallback } from 'react';
import {
    Flex,
    Text,
    View,
    IconButton,
    IconSettingsLine,
    Heading,
    List,
    Popover,
    Alert,
    Badge,
} from '@smarterservices/smarter-ui';
import { ToggleGroup, Options } from '@instructure/ui';
import DraggableModal from '@ui/shared/components/Common/DraggableModal/DraggableModal';
import useSessionStore from '../../stores/session';
import RenderName from '../Checkin/RenderName';

/**
 * SessionManager Component
 * 
 * Displays a draggable modal that shows all sessions grouped by location
 * with options to move sessions between locations.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onDismiss - Callback when modal is dismissed
 * @returns {JSX.Element} The session manager modal
 */
const SessionManager = ({ open, onDismiss }) => {
    const [openPopoverSessionSid, setOpenPopoverSessionSid] = useState(null);
    const [confirmClearSessionSid, setConfirmClearSessionSid] = useState(null);

    // Get all session locations from store
    const waiting = useSessionStore((state) => state.waiting);
    const review = useSessionStore((state) => state.review);
    const testing = useSessionStore((state) => state.testing);
    const reconnect = useSessionStore((state) => state.reconnect);
    const sentback = useSessionStore((state) => state.sentback);
    const checkout = useSessionStore((state) => state.checkout);

    /**
     * Group all sessions by location
     */
    const sessionsByLocation = useMemo(() => {
        return [
            { location: 'waiting', label: 'Waiting', sessions: Object.entries(waiting) },
            { location: 'review', label: 'Check In', sessions: Object.entries(review) },
            { location: 'reconnect', label: 'Reconnect', sessions: Object.entries(reconnect) },
            { location: 'sentback', label: 'Sent Back', sessions: Object.entries(sentback) },
            { location: 'checkout', label: 'Recently Ended', sessions: Object.entries(checkout) },
            { location: 'testing', label: 'Testing', sessions: Object.entries(testing) },
        ];
    }, [waiting, review, testing, reconnect, sentback, checkout]);

    /**
     * Handles moving a session to a different location
     */
    const handleMoveSession = useCallback((sessionSid, toLocation) => {
        useSessionStore.getState().transitionSession(sessionSid, toLocation);
        setOpenPopoverSessionSid(null);
    }, []);

    /**
     * Handles the clear session click - shows confirmation
     */
    const handleClearSessionClick = useCallback((sessionSid) => {
        setConfirmClearSessionSid(sessionSid);
    }, []);

    /**
     * Handles the confirmed clear session action
     */
    const handleConfirmClearSession = useCallback((sessionSid) => {
        useSessionStore.getState().removeFromCheckoutQueue(sessionSid);
        setConfirmClearSessionSid(null);
        setOpenPopoverSessionSid(null);
    }, []);

    /**
     * Handles popover close - resets confirmation state
     */
    const handlePopoverClose = useCallback(() => {
        setOpenPopoverSessionSid(null);
        setConfirmClearSessionSid(null);
    }, []);

    /**
     * Renders the options menu for a session
     * Only shows move options for locations the session is not currently in
     * For checkout location, shows Clear Session option with confirmation
     */
    const renderSessionMenu = useCallback((sessionSid, currentLocation) => {
        const isOpen = openPopoverSessionSid === sessionSid;
        const isConfirming = confirmClearSessionSid === sessionSid;

        return (
            <Popover
                isShowingContent={isOpen}
                onShowContent={() => setOpenPopoverSessionSid(sessionSid)}
                onHideContent={handlePopoverClose}
                on="click"
                placement="bottom start"
                shouldContainFocus
                shouldReturnFocus
                renderTrigger={
                    <IconButton
                        withBackground={false}
                        withBorder={false}
                        color="primary"
                        size="small"
                        screenReaderLabel="Session Options"
                        margin="none"
                        padding="none"
                    >
                        <IconSettingsLine />
                    </IconButton>
                }
            >
                <View as="div" padding="none" minWidth="180px">
                    <View as="div">
                        {currentLocation !== 'waiting' && (
                            <View
                                as="div"
                                padding="small"
                                cursor="pointer"
                                background="primary"
                                onClick={() => handleMoveSession(sessionSid, 'waiting')}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                <Text size="medium">Move to Waiting</Text>
                            </View>
                        )}
                        {currentLocation !== 'checkout' && (
                            <View
                                as="div"
                                padding="small"
                                cursor="pointer"
                                background="primary"
                                onClick={() => handleMoveSession(sessionSid, 'checkout')}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                <Text size="medium">Move to Recently Ended</Text>
                            </View>
                        )}
                        {currentLocation === 'checkout' && (
                            <>
                                <View
                                    as="div"
                                    padding="small"
                                    cursor="pointer"
                                    background="primary"
                                    onClick={() => handleMoveSession(sessionSid, 'testing')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <Text size="medium">Move to Testing</Text>
                                </View>
                                <View
                                    as="div"
                                    padding="small"
                                    cursor="pointer"
                                    background="primary"
                                    onClick={() => {
                                        if (isConfirming) {
                                            handleConfirmClearSession(sessionSid);
                                        } else {
                                            handleClearSessionClick(sessionSid);
                                        }
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <Text size="medium">{isConfirming ? 'Sure? Yes' : 'Clear Session'}</Text>
                                </View>
                            </>
                        )}
                    </View>
                </View>
            </Popover>
        );
    }, [openPopoverSessionSid, confirmClearSessionSid, handleMoveSession, handleClearSessionClick, handleConfirmClearSession, handlePopoverClose]);

    return (
        <DraggableModal
            isOpen={open}
            onClose={onDismiss}
            title="Session Manager"
            initialSize={{ width: 400, height: '80vh' }}
            resizable={true}
            minimizable={true}
        >
            <View as="div" padding="small">
                <Alert variant='warning' margin="none none large none">
                    Use this option only to correct sessions that were placed in the wrong area. It is not intended to be the normal or routine method for moving sessions through the room. This action will update the view for all proctors in the room.
                </Alert>
                {sessionsByLocation.map(({ location, label, sessions }) => (
                    <ToggleGroup
                        key={location}
                        toggleLabel={label}
                        padding="0 0 xxx-small 0"
                        margin="none none xxx-small none"
                        defaultExpanded={false}
                        summary={
                            <Flex justifyItems="space-between" margin="none small none none">
                                <Flex.Item>
                                    {label}
                                </Flex.Item>
                                {sessions.length > 0 && (
                                    <Flex.Item>
                                        <Badge
                                            count={sessions.length}
                                            variant="primary"
                                            standalone
                                        />
                                    </Flex.Item>
                                )}
                            </Flex>
                        }
                    >
                        {sessions.length > 0 ? (
                            <Options>
                                {sessions.map(([sessionSid, data]) => (
                                    <Options.Item
                                        key={sessionSid}
                                        beforeLabelContentVAlign="start"
                                        afterLabelContentVAlign="start"
                                    >
                                        <Flex justifyItems="space-between" padding="none">
                                            <Flex.Item>
                                                {renderSessionMenu(sessionSid, location)}
                                            </Flex.Item>
                                            <Flex.Item shouldGrow margin="none none none small">
                                                <RenderName
                                                    firstName={data.firstName}
                                                    lastName={data.lastName}
                                                />
                                                <Text size="small" color="secondary">
                                                    {' '}({sessionSid.substring(0, 8)}...)
                                                </Text>
                                            </Flex.Item>
                                        </Flex>
                                    </Options.Item>
                                ))}
                            </Options>
                        ) : (
                            <View as="div" padding="small" textAlign="center">
                                <Text size="small" color="secondary">No sessions</Text>
                            </View>
                        )}
                    </ToggleGroup>
                ))}
            </View>
        </DraggableModal>
    );
};

export default SessionManager;
